import React, { useMemo, useState } from 'react';
import { money } from '../../../utils/money';
import { BarList, Btn, KPI, KPIRow, Loading, Msg, Section, Select, useAdminGet } from '../kit';
import { S, STATUSES, STATUS_META, num, rowsOf, str } from './shared';

/**
 * Overview — the headline numbers, the two funnels (buying a report, and
 * downloading a sample) and a daily series. Everything comes from one read,
 * GET ip_intel/overview?days=N, which counts site_events (the page's
 * ipintel_* clicks) and the order / lead / download tables.
 */

type Step = {
  key: string; label: string; aliases: string[]; hint?: string;
  /** Show "N per <previous step>" rather than "% of prev" — for a count that
   *  can exceed the step before it (one form can download several PDFs). */
  per?: string;
};

const ORDER_FUNNEL: Step[] = [
  { key: 'visitors', label: 'Visited the page', aliases: ['visitors', 'page_visitors', 'page_views', 'ipintel_page_view'],
    hint: 'distinct visitors with an ipintel_page_view' },
  { key: 'order_clicks', label: 'Clicked an order button', aliases: ['order_clicks', 'click_order', 'ipintel_click_order'] },
  { key: 'payment_modal', label: 'Opened the payment sheet',
    aliases: ['payment_modal', 'payment_modal_open', 'payment_modal_opens', 'ipintel_payment_modal_open'] },
  { key: 'checkout_opened', label: 'Opened Razorpay',
    aliases: ['checkout_opened', 'payments_opened', 'ipintel_checkout_opened'] },
  { key: 'paid', label: 'Paid', aliases: ['paid', 'paid_orders', 'ipintel_payment_verified'] },
  { key: 'intake_submitted', label: 'Sent the intake', aliases: ['intake_submitted', 'intakes', 'ipintel_intake_submit'] },
  { key: 'delivered', label: 'Report delivered', aliases: ['delivered', 'delivered_orders'] },
];

const SAMPLE_FUNNEL: Step[] = [
  { key: 'selections', label: 'Ticked a sample',
    aliases: ['selections', 'sample_selections', 'sample_toggle', 'ipintel_sample_toggle'] },
  { key: 'download_clicks', label: 'Clicked download', aliases: ['download_clicks', 'ipintel_click_download'] },
  { key: 'leads', label: 'Filled the form', aliases: ['leads', 'sample_leads', 'lead_submits', 'ipintel_lead_submit'] },
  // Forms whose reports reached them: e-mailed, or at least one PDF
  // downloaded (an older server sends PDFs downloaded here instead).
  { key: 'received', label: 'Received the reports', aliases: ['received', 'samples_received', 'downloads', 'sample_downloads'],
    hint: 'forms whose reports were e-mailed or downloaded' },
];

/** The page's order-button positions and tracked sections, in words. */
const POSITION_LABELS: Record<string, string> = {
  header: 'Header “Order a report”', hero: 'Hero “Get your report”', scope_card: 'Scope grid order card',
  cta_band: 'Dark CTA band', nav: 'Site navigation',
};
const SECTION_LABELS: Record<string, string> = {
  scope: 'Scope — seven questions', trusted: '“Trusted by” logos', samples: 'Sample reports', how: 'How it works',
  faq: 'FAQ', bulk: 'Bulk-pricing strip', cta: 'Closing CTA band',
};

const SERIES: { key: string; label: string; aliases: string[]; money?: boolean }[] = [
  { key: 'visitors', label: 'Visitors', aliases: ['visitors', 'page_visitors', 'page_views'] },
  { key: 'order_clicks', label: 'Order clicks', aliases: ['order_clicks'] },
  { key: 'checkout_opened', label: 'Razorpay opened', aliases: ['checkout_opened'] },
  { key: 'paid', label: 'Paid orders', aliases: ['paid', 'paid_orders', 'orders_paid'] },
  { key: 'revenue', label: 'Revenue', aliases: ['revenue_minor', 'revenue_total_minor', 'revenue'], money: true },
  { key: 'leads', label: 'Sample leads', aliases: ['leads', 'sample_leads'] },
  { key: 'downloads', label: 'Downloads', aliases: ['downloads', 'sample_downloads'] },
  { key: 'emailed', label: 'Samples e-mailed', aliases: ['emailed', 'samples_emailed'] },
];

/** One step's count out of a funnel given as [{key,count}], {key: count}, or the KPIs. */
const stepCount = (step: Step, funnel: any, kpis: any): { count: number; label?: string; found: boolean } => {
  if (Array.isArray(funnel)) {
    const hit = funnel.find(f => [f?.key, f?.stage, f?.step, f?.action].some(k => step.aliases.includes(str(k)) || str(k) === step.key));
    if (hit) return { count: num(hit.count ?? hit.value ?? hit.people ?? hit.n), label: hit.label, found: true };
  } else if (funnel && typeof funnel === 'object') {
    for (const a of [step.key, ...step.aliases]) {
      if (funnel[a] !== undefined) return { count: num(funnel[a]), found: true };
    }
  }
  for (const a of [step.key, ...step.aliases]) {
    if (kpis && kpis[a] !== undefined) return { count: num(kpis[a]), found: true };
  }
  return { count: 0, found: false };
};

const pick = (o: any, ...keys: string[]): any => {
  for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
};

/** {label, value}[] out of [{key|action|label, count}] or {label: count}. */
const breakdown = (v: any, names: Record<string, string> = {}): { label: string; value: number }[] => {
  const name = (k: string) => names[k] || k;
  if (Array.isArray(v)) {
    return v.map(x => ({ label: name(str(x?.label ?? x?.key ?? x?.position ?? x?.section ?? x?.action ?? x?.name)),
                         value: num(x?.count ?? x?.value ?? x?.n) }))
      .filter(x => x.label).sort((a, b) => b.value - a.value);
  }
  if (v && typeof v === 'object') {
    return Object.entries(v).map(([label, value]) => ({ label: name(label), value: num(value) }))
      .sort((a, b) => b.value - a.value);
  }
  return [];
};

const FunnelBars: React.FC<{ steps: Step[]; funnel: any; kpis: any; accentLast?: boolean }> =
  ({ steps, funnel, kpis, accentLast }) => {
  const rows = steps.map(s => {
    const c = stepCount(s, funnel, kpis);
    return { ...s, label: c.label || s.label, count: c.count, found: c.found };
  });
  const max = Math.max(1, ...rows.map(r => r.count));
  if (!rows.some(r => r.found)) {
    return <div className="text-sm text-grayscale-1100 py-4 text-center">No funnel data yet.</div>;
  }
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((r, i) => {
        const prev = i > 0 ? rows[i - 1].count : 0;
        const pct = i > 0 && prev ? Math.round((r.count / prev) * 100) : null;
        const note = r.per ? (prev ? ` (${(r.count / prev).toFixed(1)} per ${r.per})` : '')
          : pct === null ? '' : ` (${pct}% of prev)`;
        return (
          <div key={r.key} className="flex items-center gap-2 text-sm" title={r.hint || r.label}>
            <div className="w-28 sm:w-52 shrink-0 truncate text-grayscale-1200">
              <span className="text-grayscale-1100 text-xs mr-1">{i + 1}.</span>{r.label}
            </div>
            <div className="flex-1 bg-grayscale-200 rounded-full h-3 overflow-hidden">
              <div className={`h-3 rounded-full ${accentLast && i === rows.length - 1 ? 'bg-green-600' : 'bg-primary-500'}`}
                style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
            <div className="w-24 sm:w-32 shrink-0 text-right text-grayscale-1300 font-medium whitespace-nowrap">
              {r.found ? r.count.toLocaleString() : '—'}
              <span className="text-grayscale-1100 text-[10px] sm:text-xs">{note}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Daily bars for one metric at a time: single hue, a readout line instead of
 *  a floating tooltip (it never covers a neighbouring bar), and the exact
 *  value in each bar's title for keyboard / screen-reader users. */
const DailyBars: React.FC<{ days: any[]; currency: string }> = ({ days, currency }) => {
  const [metric, setMetric] = useState('visitors');
  const [hover, setHover] = useState<number | null>(null);
  const spec = SERIES.find(s => s.key === metric) || SERIES[0];
  const pts = days.map(d => ({
    day: str(d?.day ?? d?.date ?? d?.d),
    value: num(pick(d, ...spec.aliases)),
  }));
  const max = Math.max(1, ...pts.map(p => p.value));
  const fmt = (v: number) => spec.money ? money(v, currency) : v.toLocaleString();
  const dayLabel = (s: string) => {
    const t = new Date(s);
    return isNaN(t.getTime()) ? s : t.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };
  const total = pts.reduce((a, p) => a + p.value, 0);
  const shown = hover !== null ? pts[hover] : null;
  const available = SERIES.filter(s => days.some(d => pick(d, ...s.aliases) !== undefined));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {(available.length ? available : SERIES).map(s => (
            <button key={s.key} type="button" onClick={() => setMetric(s.key)}
              className={`py-1 px-2.5 rounded-full text-xs font-grotesk whitespace-nowrap ${metric === s.key
                ? 'bg-primary-500 text-grayscale-100 font-bold'
                : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-grayscale-1100 min-h-[1rem]" aria-live="polite">
          {shown ? <><span className="font-bold text-grayscale-1300">{fmt(shown.value)}</span> {spec.label.toLowerCase()} on {dayLabel(shown.day)}</>
            : <>Total <span className="font-bold text-grayscale-1300">{fmt(total)}</span> over {pts.length} days</>}
        </div>
      </div>
      {pts.length === 0 ? (
        <div className="text-sm text-grayscale-1100 py-4 text-center">No daily data yet.</div>
      ) : (
        <div className="flex gap-2">
          <div className="flex flex-col justify-between text-[10px] text-grayscale-1100 text-right w-14 shrink-0 h-40">
            <span>{fmt(max)}</span><span>{fmt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="relative h-40 border-b border-grayscale-300"
              onMouseLeave={() => setHover(null)}>
              <div aria-hidden className="absolute inset-x-0 top-0 border-t border-dashed border-grayscale-200" />
              <div aria-hidden className="absolute inset-x-0 top-1/2 border-t border-dashed border-grayscale-200" />
              <div className="absolute inset-0 flex items-end gap-[2px]">
                {pts.map((p, i) => (
                  <div key={p.day || i} className="flex-1 h-full flex items-end cursor-default"
                    onMouseEnter={() => setHover(i)}
                    title={`${dayLabel(p.day)}: ${fmt(p.value)}`}>
                    <div className={`w-full rounded-t ${hover === i ? 'bg-primary-700' : 'bg-primary-500'}`}
                      style={{ height: p.value ? `max(2px, ${(p.value / max) * 100}%)` : '0' }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between text-[10px] text-grayscale-1100 mt-1">
              <span>{dayLabel(pts[0].day)}</span>
              {pts.length > 2 && <span>{dayLabel(pts[Math.floor(pts.length / 2)].day)}</span>}
              <span>{dayLabel(pts[pts.length - 1].day)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Overview: React.FC<{
  onOpenOrders: (status: string) => void;
  /** Opens the Bulk enquiries tab, filtered to one status ('' = all). */
  onOpenBulk?: (status: string) => void;
}> = ({ onOpenOrders, onOpenBulk }) => {
  const [days, setDays] = useState('30');
  const ov = useAdminGet(S, 'overview', { days }, [days]);
  const d = ov.data || {};
  const k = d.kpis || d.kpi || d;

  const currency = str(pick(k, 'currency', 'revenue_currency') ?? d.currency) || 'INR';
  // Revenue per currency once a second region sells in another; one line today.
  const revenueRows: { currency: string; total: number; base: number; tax: number }[] = useMemo(() => {
    const arr = Array.isArray(d.revenue) ? d.revenue : Array.isArray(k.revenue) ? k.revenue : null;
    if (arr) {
      return arr.map((r: any) => ({
        currency: str(r.currency) || 'INR',
        total: num(r.total_minor ?? r.incl_tax_minor), base: num(r.base_minor ?? r.excl_tax_minor),
        tax: num(r.tax_minor),
      }));
    }
    const total = num(pick(k, 'revenue_incl_tax_minor', 'revenue_total_minor', 'revenue_minor'));
    const base = num(pick(k, 'revenue_excl_tax_minor', 'revenue_base_minor'));
    return [{ currency, total, base, tax: num(pick(k, 'revenue_tax_minor')) || Math.max(0, total - base) }];
  }, [d, k, currency]);

  const statusCounts: Record<string, number> = useMemo(() => {
    const v = d.status_counts || d.by_status || k.status_counts;
    if (Array.isArray(v)) return Object.fromEntries(v.map((x: any) => [str(x.status), num(x.count ?? x.n)]));
    if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([s, n]) => [s, num(n)]));
    return {};
  }, [d, k]);
  const hasStatus = Object.keys(statusCounts).length > 0;

  const avgTurn = pick(k, 'avg_turnaround_days', 'turnaround_avg_days');
  const overdue = pick(k, 'overdue', 'overdue_orders') ?? d.overdue;
  const byDay = rowsOf(d.by_day ?? d.series ?? d.daily);
  const positions = breakdown(d.order_clicks_by_position ?? d.click_positions ?? k.order_clicks_by_position, POSITION_LABELS);
  const sections = breakdown(d.section_views ?? d.sections, SECTION_LABELS);
  const regions = breakdown(d.visitors_by_region ?? d.regions);

  // {total, new, last_30d} — the "Contact us for bulk pricing" form's enquiries.
  const bulk = d.bulk_enquiries ?? k.bulk_enquiries;

  const kpi = (...keys: string[]) => {
    const v = pick(k, ...keys);
    return v === undefined ? '—' : num(v).toLocaleString();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-grayscale-1100">
          Clicks come from the page's own tracking (site events named <code>ipintel_*</code>); orders,
          leads and downloads from their tables. Team accounts are {d.team_excluded === false ? 'included' : 'excluded'}.
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onChange={setDays} options={['7', '30', '90', '365']}
            labels={{ '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days', '365': 'Last 12 months' }} />
          <Btn small onClick={ov.reload} disabled={ov.loading}>Refresh</Btn>
        </div>
      </div>
      {ov.error && <Msg kind="error">{ov.error}</Msg>}
      {ov.loading && !ov.data ? <Loading /> : (
        <>
          <KPIRow>
            <KPI label="Page visitors" value={kpi('page_visitors', 'visitors')} hint={`last ${days} days`} />
            <KPI label="Order clicks" value={kpi('order_clicks')} />
            <KPI label="Payments opened" value={kpi('payments_opened', 'checkout_opened')} hint="Razorpay sheet opened" />
            <KPI label="Paid orders" value={kpi('paid_orders', 'paid')}
              onClick={() => onOpenOrders('')} hint="click to list orders" />
            {revenueRows.map(r => (
              <KPI key={r.currency} label={`Revenue${revenueRows.length > 1 ? ` · ${r.currency}` : ''}`}
                value={money(r.total, r.currency)}
                hint={`${money(r.base, r.currency)} excl. tax · ${money(r.tax, r.currency)} tax${
                  num(k.refunded_minor) > 0 && r.currency === currency ? ` · ${money(num(k.refunded_minor), currency)} refunded` : ''}`} />
            ))}
          </KPIRow>
          <KPIRow>
            <KPI label="Intake submitted" value={kpi('intake_submitted', 'intakes')}
              onClick={() => onOpenOrders('intake_submitted')} hint="awaiting start — click to list" />
            <KPI label="Delivered" value={kpi('delivered')} onClick={() => onOpenOrders('delivered')} />
            <KPI label="Avg turnaround" value={avgTurn === undefined || avgTurn === null ? '—' : `${num(avgTurn).toFixed(1)} d`}
              hint="intake → delivered" />
            {overdue !== undefined && (
              <KPI label="Overdue" value={num(overdue).toLocaleString()}
                hint={`past the ${num(d.turnaround_days) || 7}-day promise`} onClick={() => onOpenOrders('overdue')} />
            )}
            <KPI label="Sample leads" value={kpi('sample_leads', 'leads')} />
            <KPI label="Sample downloads" value={kpi('downloads', 'sample_downloads')} />
            {k.samples_emailed !== undefined && (
              <KPI label="Samples e-mailed" value={kpi('samples_emailed')}
                hint={num(k.samples_not_emailed) ? `${num(k.samples_not_emailed).toLocaleString()} not sent` : undefined} />
            )}
            {bulk && typeof bulk === 'object' && (
              <KPI label="Bulk enquiries"
                value={<>{num(bulk.new).toLocaleString()} new <span className="text-sm font-normal text-grayscale-1100">/ {num(bulk.total).toLocaleString()}</span></>}
                hint={`${num(bulk.last_30d).toLocaleString()} in the last 30 days${onOpenBulk ? ' — click to list new' : ''}`}
                onClick={onOpenBulk ? () => onOpenBulk(num(bulk.new) > 0 ? 'new' : '') : undefined} />
            )}
          </KPIRow>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title="Order funnel">
              <FunnelBars steps={ORDER_FUNNEL} funnel={d.funnel ?? d.order_funnel} kpis={k} accentLast />
              <p className="text-xs text-grayscale-1100">
                Steps 1–4 are distinct visitors from the page's click tracking; 5–7 are orders.
              </p>
            </Section>
            <Section title="Sample-report funnel">
              <FunnelBars steps={SAMPLE_FUNNEL} funnel={d.samples_funnel ?? d.sample_funnel} kpis={k} />
              <p className="text-xs text-grayscale-1100">
                A lead is one form submission; one lead can download several PDFs.
              </p>
            </Section>
          </div>

          <Section title={`By day — last ${days} days`}>
            <DailyBars days={byDay} currency={currency} />
          </Section>

          {(hasStatus || positions.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {hasStatus && (
                <Section title="Orders by status">
                  <BarList
                    items={STATUSES.filter(s => statusCounts[s] !== undefined)
                      .map(s => ({ label: STATUS_META[s].label, value: statusCounts[s] }))}
                    onSelect={l => {
                      const s = STATUSES.find(x => STATUS_META[x].label === l);
                      if (s) onOpenOrders(s);
                    }} />
                  <p className="text-xs text-grayscale-1100">Click a status to list those orders.</p>
                </Section>
              )}
              {positions.length > 0 && (
                <Section title="Which order button gets clicked">
                  <BarList items={positions} />
                </Section>
              )}
            </div>
          )}
          {(sections.length > 0 || regions.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {sections.length > 0 && (
                <Section title="How far people scroll (section seen)">
                  <BarList items={sections} />
                </Section>
              )}
              {regions.length > 0 && (
                <Section title="Visitors by pricing region">
                  <BarList items={regions} />
                </Section>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Overview;
